/**
 * ActiveJobsContext — Provides active Firestore jobs + local pending job state.
 *
 * Wraps useActiveJobs and adds pending job tracking for submission states
 * (the gap between user action and Firestore job creation).
 *
 * Pending jobs are keyed by documentId and auto-clear when a matching
 * Firestore job appears.
 *
 * See: docs/features/infrastructure/notification-system.md (Phase 2a)
 */
import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from "react";
import { useActiveJobs } from "../hooks/useActiveJobs";
import { invalidatePatientDirectory } from "../utils/patientDirectoryService";

const ActiveJobsContext = createContext(null);

export function ActiveJobsProvider({ userId, children }) {
  // Track job data so we have patientId available when a job completes
  // (jobs leave the snapshot query on completion, losing their data)
  const jobDataRef = useRef(new Map());

  const { jobs: activeJobs, activeCount, dismiss } = useActiveJobs({
    userId,
    callbacks: {
      onJobComplete: (job) => {
        const cached = jobDataRef.current.get(job.id);
        const patientId = cached?.patientId;
        if (patientId) {
          invalidatePatientDirectory(userId);
          window.dispatchEvent(
            new CustomEvent("refreshPatientDocuments", {
              detail: { patientId, action: "documentCompleted" },
            })
          );
        }
        jobDataRef.current.delete(job.id);
      },
    },
  });

  // Keep jobDataRef in sync with active jobs
  useEffect(() => {
    activeJobs.forEach((job) => {
      jobDataRef.current.set(job.id, { patientId: job.patientId, documentId: job.documentId });
    });
  }, [activeJobs]);

  const [pendingJobs, setPendingJobs] = useState([]);

  const addPendingJob = useCallback((documentId, label, patientId = null, patientName = "") => {
    setPendingJobs((prev) => [...prev, { id: documentId, label, patientId, patientName, createdAt: Date.now() }]);
  }, []);

  const removePendingJob = useCallback((documentId) => {
    setPendingJobs((prev) => prev.filter((job) => job.id !== documentId));
  }, []);

  // Auto-clear pending jobs when matching Firestore job appears
  useEffect(() => {
    if (pendingJobs.length === 0 || activeJobs.length === 0) return;
    setPendingJobs((prev) =>
      prev.filter((pending) => !activeJobs.some((active) => active.documentId === pending.id))
    );
  }, [activeJobs]);

  const value = {
    activeJobs,
    activeCount,
    dismiss,
    pendingJobs,
    addPendingJob,
    removePendingJob,
  };

  return (
    <ActiveJobsContext.Provider value={value}>
      {children}
    </ActiveJobsContext.Provider>
  );
}

export function useActiveJobsContext() {
  const ctx = useContext(ActiveJobsContext);
  if (!ctx) {
    throw new Error("useActiveJobsContext must be used within an ActiveJobsProvider");
  }
  return ctx;
}
